import { IDomServices } from 'global/interfaces/controller.interface';
import { Subject } from 'rxjs';

export class DomServices<T> implements IDomServices<T> {

	protected $element: HTMLElement;
	protected $references: Record<keyof T, HTMLElement>;
	onDomElementChanged: Subject<string> = new Subject();

	constructor(element: HTMLElement, references: Record<keyof T, HTMLElement>) {
		this.$references = references;
		this.$element = element;
	}

	static collectReferences<T>(element: HTMLElement, ignoredReferences: string[], includedReferences: string[]): Partial<Record<keyof T, HTMLElement>> {
		const refs: Partial<Record<keyof T, HTMLElement>> = {};

		Array.from(element.querySelectorAll('[data-ref]')).forEach((el: HTMLElement) => {
			let referenceName: string = '';
			if (el.dataset.ref) {
				referenceName = el.dataset.ref;
			}

			// skip ignored references
			if (ignoredReferences.includes(referenceName)) {
				return;
			}

			if (referenceName) {
				// if the included list has items, only include these references, otherwise consider all
				if (includedReferences.length > 0) {
					if (includedReferences.includes(referenceName)) {
						refs[referenceName] = el;
					}
				} else {
					if (Object.prototype.hasOwnProperty.call(refs, referenceName)) {
						if (window.location.hostname === 'localhost') {
							console.error(
								`A reference called '${referenceName}' has already been registered in ${this.constructor.name}!\n Element:`,
								element,
							);
						}
					} else {
						refs[referenceName] = el;
					}
				}
			}
		});
		return refs;
	}

	getRef<T extends HTMLElement = HTMLElement>(ref: string): T | undefined {
		if (this.$references) {
			return Object.prototype.hasOwnProperty.call(this.$references, ref) ? (this.$references[ref] as T) : undefined;
		}
		return undefined;
	}

	parseStringToHtml = (htmlAsString: string): void | ChildNode => {
		const parsedNode = new DOMParser().parseFromString(htmlAsString, 'text/html').body.firstChild;
		if (parsedNode) return parsedNode
	}

	renderDomElement = (ref: string, html: string, renderOnParent: boolean = false): void => {
		const refExists = this.$references ? Object.prototype.hasOwnProperty.call(this.$references, ref) : undefined;
		if (renderOnParent) {
			this.$element.innerHTML = html;
			this.domElementChanged(ref);
		} else if (this.$references && refExists) {
			(this.$references as any)[ref].innerHTML = html;
			this.domElementChanged(ref);
		} else {
			console.warn('DOM ref does not exist!', ref);
		}
	}

	appendRawHtmlToDomElement = (node: HTMLElement, htmlAsString: string, position: 'beforebegin' | 'afterbegin' | 'beforeend' | 'afterend' = 'afterend'): void | ChildNode => {
		const htmlNode = this.parseStringToHtml(htmlAsString)
		node.insertAdjacentElement(position, htmlNode as Element);
		if (htmlNode) {
			return htmlNode
		}
	}

	replaceLastChildElementWithRawHtml = (ref: string, htmlAsString: string): void | ChildNode => {
		const refExists = this.$references ? Object.prototype.hasOwnProperty.call(this.$references, ref) : undefined;
		if (refExists) {
			const htmlNode = this.parseStringToHtml(htmlAsString);
			const lastNode = this.$references[ref].childNodes[this.$references[ref].childNodes.length - 1];
			(this.$references as any)[ref].replaceChild(htmlNode, lastNode);
			this.domElementChanged(ref);
			if (htmlNode) {
				return htmlNode
			}
		} else {
			console.warn('DOM ref does not exist!', ref);
		}
	}

	toggleElementState(control: HTMLElement, newState: Array<'enable' | 'disable' | 'show' | 'hide' | 'check' | 'uncheck'>): void {
		if (!control) return;
		newState && newState.forEach(state => {
			switch (state) {
				case 'enable': {
					control.removeAttribute('disabled');
					break;
				}
				case 'disable': {
					control.setAttribute('disabled', 'true');
					break;
				}
				case 'show': {
					control.removeAttribute('hidden');
					break;
				}
				case 'hide': {
					control.setAttribute('hidden', 'true');
					break;
				}
				case 'uncheck': {
					control.removeAttribute('checked');
					break;
				}
				case 'check': {
					control.setAttribute('checked', 'true');
					break;
				}
				default: break;
			}
		});
	}

	domElementChanged = (reference: string): void => {
		this.onDomElementChanged.next(reference);
	}

	parseDOMScriptTag = <TJSON>(elementId: string): TJSON => {
		const data = document.getElementById(elementId) as HTMLScriptElement;
		try {
			const out = JSON.parse(data.innerText);
			return out as TJSON;
		} catch (error) {
			console.warn('Cannot parse component form config', error)
		}
		return {} as TJSON;
	}

	renderDomElementInHTMLElement(element: HTMLElement, html: string): void {
		element.innerHTML = html;
	}
}
